import {
  ChargeMode,
  CreateTransactionDTO,
  TransactionFilters,
  TransactionQueryResult,
  TransactionTypeQueryResult,
  UpdateTransactionDTO,
  StreetCentsMetrics,
} from 'src/types/transaction.types';
import { Json } from 'src/types/db.types';
import { supabase } from './supabase/client';
import { savingsAccountService } from './savingsAccount.service';

import { resolveAssignedStudentIdsForTeacher } from './student.service';

const TABLE_NAME = 'transactions';
const TRANSACTION_TYPES_TABLE = 'transaction_types';

function applyTransactionDateFilters(query: any, startDate?: Date, endDate?: Date) {
  let q = query;
  if (startDate) q = q.gte('createdat', startDate.toISOString());
  if (endDate) q = q.lte('createdat', endDate.toISOString());
  return q;
}

function applyTransactionFilters(
  query: any,
  assignedStudentIds: string[] | null,
  filters?: TransactionFilters
) {
  let q = query;
  if (assignedStudentIds !== null) q = q.in('studentid', assignedStudentIds);
  if (!filters) return q;

  const { studentId, type, status, startDate, endDate, transactionTypeId, search } = filters;
  if (studentId) q = q.eq('studentid', studentId);
  if (transactionTypeId) q = q.eq('transactiontypeid', transactionTypeId);
  if (type) q = q.eq('transaction_type.type', type);
  if (status && status !== 'ALL') q = q.eq('status', status);
  q = applyTransactionDateFilters(q, startDate, endDate);
  if (search) {
    q = q.or(`firstname.ilike.%${search}%,lastname.ilike.%${search}%,email.ilike.%${search}%`, {
      foreignTable: 'student.user',
    });
  }
  return q;
}

function calculateStreetCentsMetrics(items: any[]): StreetCentsMetrics {
  let totalCredits = 0;
  let totalDebits = 0;
  let pendingCount = 0;

  items.forEach((item) => {
    const amt = Number(item.amount) || 0;
    const type = item.transaction_type?.type;
    const status = item.status;

    if (status === 'Pending') {
      pendingCount += 1;
    }
    if (status === 'Approved' || !status) {
      if (type === 'credit') totalCredits += amt;
      else if (type === 'debit') totalDebits += amt;
    }
  });

  return {
    totalTransactions: items.length,
    totalCredits,
    totalDebits,
    netVolume: totalCredits - totalDebits,
    pendingCount,
  };
}

export const transactionService = {
  /**
   * Get transactions with pagination and filters
   */
  async getTransactions(page: number, limit: number, filters?: TransactionFilters): Promise<TransactionQueryResult> {
    try {
      const assignedStudentIds = await resolveAssignedStudentIdsForTeacher({
        teacherId: filters?.teacherId,
        teacherUserId: filters?.teacherUserId,
        scope: filters?.scope,
      });

      if (assignedStudentIds !== null && assignedStudentIds.length === 0) {
        return { data: [], total: 0, page, limit };
      }

      let query = supabase
        .from(TABLE_NAME)
        .select(
          `
          *,
          transaction_type:transaction_types(*),
          creator:users!transactions_createdby_fkey(id, firstname, lastname, email, teachers(id)),
          student: students!inner(
            *,
            user: users!inner(*)
          )
        `,
          { count: 'exact' }
        )
        .is('deletedat', null)
        .is('student.user.deletedat', null);

      query = applyTransactionFilters(query, assignedStudentIds, filters);

      // Add pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      query = query.order('createdat', { ascending: false }).range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data || [],
        total: count || 0,
        page,
        limit,
      };
    } catch (error) {
      console.error('[TransactionService.getTransactions]:', error);
      throw error;
    }
  },

  /**
   * Get Street Cents KPI analytics metrics
   */
  async getStreetCentsMetrics(filters?: TransactionFilters): Promise<StreetCentsMetrics> {
    try {
      const assignedStudentIds = await resolveAssignedStudentIdsForTeacher({
        teacherId: filters?.teacherId,
        teacherUserId: filters?.teacherUserId,
        scope: filters?.scope,
      });

      if (assignedStudentIds !== null && assignedStudentIds.length === 0) {
        return {
          totalTransactions: 0,
          totalCredits: 0,
          totalDebits: 0,
          netVolume: 0,
          pendingCount: 0,
        };
      }

      let query = supabase
        .from(TABLE_NAME)
        .select(
          `
          id,
          amount,
          status,
          transaction_type:transaction_types(type),
          student:students!inner(
            user:users!inner(deletedat)
          )
        `
        )
        .is('deletedat', null)
        .is('student.user.deletedat', null);

      if (assignedStudentIds !== null) {
        query = query.in('studentid', assignedStudentIds);
      }

      if (filters?.studentId) {
        query = query.eq('studentid', filters.studentId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return calculateStreetCentsMetrics(data || []);
    } catch (error) {
      console.error('[TransactionService.getStreetCentsMetrics]:', error);
      return {
        totalTransactions: 0,
        totalCredits: 0,
        totalDebits: 0,
        netVolume: 0,
        pendingCount: 0,
      };
    }
  },

  /**
   * Get a single transaction by ID with full relational details
   */
  async getTransactionById(id: string) {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select(
          `
          *,
          transaction_type:transaction_types(*),
          creator:users!transactions_createdby_fkey(id, firstname, lastname, email, teachers(id)),
          student: students(
            *,
            user: users(*)
          )
        `
        )
        .eq('id', id)
        .single();

      if (error) throw new Error(error.message);
      return data;
    } catch (error) {
      console.error('[TransactionService.getTransactionById]:', error);
      throw error;
    }
  },

  /**
   * Get transaction types with pagination
   */
  async getTransactionTypes(
    page: number,
    limit: number,
    type?: 'debit' | 'credit',
    search?: string
  ): Promise<TransactionTypeQueryResult> {
    try {
      let query = supabase
        .from(TRANSACTION_TYPES_TABLE)
        .select('*, creator:users!transaction_types_createdby_fkey(id, firstname, lastname, email, teachers(id))', { count: 'exact' });

      if (type) {
        query = query.eq('type', type);
      }

      if (search) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      // Add pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      // deletedat is null
      query = query.is('deletedat', null);

      query = query.order('createdat', { ascending: false }).range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data || [],
        total: count || 0,
        page,
        limit,
      };
    } catch (error) {
      console.error('[TransactionService.getTransactionTypes]:', error);
      throw error;
    }
  },

  /**
   * Create a new transaction. Returns its id, and settled: false when it waits for the student (a bill).
   */
  async createTransaction(dto: CreateTransactionDTO): Promise<{ id: string; settled: boolean }> {
    try {
      // 1. Get current balance (for reference only)
      const { data: student, error: studentError } = await supabase
        .from('students')
        .select('balance')
        .eq('id', dto.studentId)
        .single();

      if (studentError) throw studentError;
      if (!student) throw new Error('Student not found');

      // 2. Get transaction type
      const { data: transactionType, error: typeError } = await supabase
        .from(TRANSACTION_TYPES_TABLE)
        .select('*')
        .eq('id', dto.transactionTypeId)
        .single();

      if (typeError) throw typeError;
      if (!transactionType) throw new Error('Transaction type not found');

      // A credit is approved at once and split between savings and balance. create_credit does
      // the split, the balance update and the record in one database call, so it cannot half-apply.
      if (transactionType.type === 'credit') {
        const { data: creditId, error: creditError } = await supabase.rpc('create_credit', {
          p_student_id: dto.studentId,
          p_type_id: dto.transactionTypeId,
          p_amount: dto.amount,
          p_description: dto.description ?? null,
          p_reason: dto.reason ?? null,
          p_extended: (dto.extendedProperties ?? {}) as Json,
        });
        if (creditError) throw creditError;
        return { id: creditId as string, settled: true };
      }

      // An auto debit (fine, tax, purchase) is taken at once. create_debit records it and moves the
      // balance in one database call, so it cannot half-apply.
      if (transactionType.charge_mode === 'auto') {
        const { data: debitId, error: debitError } = await supabase.rpc('create_debit', {
          p_student_id: dto.studentId,
          p_type_id: dto.transactionTypeId,
          p_amount: dto.amount,
          p_description: dto.description ?? null,
          p_reason: dto.reason ?? null,
          p_extended: (dto.extendedProperties ?? {}) as Json,
        });
        if (debitError) throw debitError;
        return { id: debitId as string, settled: true };
      }

      // A bill waits for the student. The balance changes only when they pay it.
      const transactionData = {
        studentid: dto.studentId,
        transactiontypeid: dto.transactionTypeId,
        amount: dto.amount,
        description: dto.description,
        reason: dto.reason,
        balanceafter: (student.balance || 0) - dto.amount,
        status: 'Pending', // Keep legacy status for backward compatibility
        transaction_status: 'PENDING_STUDENT_ACTION', // New status for student-led workflow
        original_amount: dto.amount,
        extendedproperties: {
          originalAmount: dto.amount,
          ...dto.extendedProperties,
          previousBalance: student.balance,
          transactionType: transactionType,
        },
      };

      const { data: inserted, error: insertError } = await supabase
        .from(TABLE_NAME)
        .insert(transactionData)
        .select('id')
        .single();

      if (insertError) throw insertError;
      if (!inserted?.id) throw new Error('Failed to create transaction');

      return { id: inserted.id, settled: false };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Approve a transaction and update student balance
   */
  async approveTransaction(transactionId: string): Promise<void> {
    try {
      // 1. Get the transaction
      const { data: transaction, error: transactionError } = await supabase
        .from(TABLE_NAME)
        .select(
          `
          *,
          transaction_type:transaction_types(*)
        `
        )
        .eq('id', transactionId)
        .single();

      if (transactionError) throw transactionError;
      if (!transaction) throw new Error('Transaction not found');
      if (transaction.status !== 'Pending') {
        throw new Error('Can only approve pending transactions');
      }

      // 2. Get current student balance
      const { data: student, error: studentError } = await supabase
        .from('students')
        .select('balance')
        .eq('id', transaction.studentid)
        .single();

      if (studentError) throw studentError;
      if (!student) throw new Error('Student not found');

      // 3. Calculate new balance
      const balanceChange = transaction.transaction_type.type === 'credit' ? transaction.amount : -transaction.amount;
      const newBalance = (student.balance || 0) + balanceChange;

      // 4. Update transaction status
      const { error: updateError } = await supabase
        .from(TABLE_NAME)
        .update({
          status: 'Approved',
          transaction_status: 'APPROVED',
          updatedat: new Date().toISOString(),
        })
        .eq('id', transactionId)
        .eq('status', 'Pending');

      if (updateError) throw updateError;

      // 5. Update student balance
      const { error: balanceError } = await supabase
        .from('students')
        .update({
          balance: newBalance,
          updatedat: new Date().toISOString(),
        })
        .eq('id', transaction.studentid);

      if (balanceError) throw balanceError;
    } catch (error) {
      console.error('[TransactionService.approveTransaction] Error:', error);
      throw error;
    }
  },

  /**
   * Cancel a pending transaction
   */
  async cancelTransaction(transactionId: string): Promise<void> {
    try {
      console.log('[TransactionService.cancelTransaction]:', transactionId);
      const { error } = await supabase
        .from(TABLE_NAME)
        .update({
          status: 'cancelled',
          transaction_status: 'REJECTED',
          updatedat: new Date().toISOString(),
        })
        .eq('id', transactionId)
        .eq('status', 'Pending');

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async rejectTransaction(transactionId: string): Promise<void> {
    try {
      const { error } = await supabase.from(TABLE_NAME).update({ status: 'rejected' }).eq('id', transactionId);

      if (error) throw error;
    } catch (error) {
      console.error('[TransactionService.rejectTransaction]:', error);
      throw error;
    }
  },

  /**
   * Update a transaction
   */
  async updateTransaction(dto: UpdateTransactionDTO): Promise<void> {
    try {
      const { id, ...updateData } = dto;
      const { error } = await supabase.from(TABLE_NAME).update(updateData).eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('[TransactionService.updateTransaction]:', error);
      throw error;
    }
  },

  /**
   * Delete a transaction permanently
   */
  async deleteTransaction(id: string): Promise<void> {
    console.log('[TransactionService.deleteTransaction]:', id);
    try {
      const { error } = await supabase.from(TABLE_NAME).delete().eq('id', id);

      if (error) {
        console.error('[TransactionService.deleteTransaction] Error:', error);
        throw error;
      }
    } catch (error) {
      console.error('[TransactionService.deleteTransaction]:', error);
      throw error;
    }
  },

  /**
   * Create a new transaction type
   */
  async createTransactionType(data: {
    name: string;
    description: string;
    type: 'debit' | 'credit';
    priceType: 'fixed' | 'variable';
    points: number;
    chargeMode?: ChargeMode;
  }): Promise<void> {
    try {
      console.log('[TransactionService.createTransactionType]:', data);
      const { error } = await supabase.from(TRANSACTION_TYPES_TABLE).insert({
        name: data.name,
        description: data.description,
        type: data.type,
        points: data.points,
        priceType: data.priceType,
        charge_mode: data.chargeMode ?? 'auto',
      });

      if (error) throw error;
    } catch (error) {
      console.error('[TransactionService.createTransactionType]:', error);
      throw error;
    }
  },

  /**
   * Update a transaction type
   */
  async updateTransactionType(
    id: string,
    data: {
      name: string;
      description: string;
      type: 'debit' | 'credit';
      points: number | null;
      priceType: 'fixed' | 'variable';
      chargeMode?: ChargeMode;
    }
  ): Promise<void> {
    try {
      console.log('[TransactionService.updateTransactionType]:', { id, data });

      const { error } = await supabase
        .from(TRANSACTION_TYPES_TABLE)
        .update({
          name: data.name,
          description: data.description,
          type: data.type,
          points: data.points,
          priceType: data.priceType,
          ...(data.chargeMode && { charge_mode: data.chargeMode }),
          updatedat: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        console.error('[TransactionService.updateTransactionType] Error:', error);
        throw error;
      }
    } catch (error) {
      console.error('[TransactionService.updateTransactionType] Error:', error);
      throw error;
    }
  },

  /**
   * Delete a transaction type (soft delete)
   */
  async deleteTransactionType(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(TRANSACTION_TYPES_TABLE)
        .update({ deletedat: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('[TransactionService.deleteTransactionType]:', error);
      throw error;
    }
  },

  /**
   * Get all active students with their current points
   */
  async getActiveStudents(): Promise<
    Array<{
      id: string;
      name: string;
      currentPoints: number;
      userId: string;
    }>
  > {
    try {
      const { data, error } = await supabase
        .from('students')
        .select(
          `*,user:users!inner(
            id,
            firstname,
            lastname,
            role,
            deletedat
          )
        `
        )
        // Every students row is a scholar, including Student Bankers, so no role filter.
        .is('user.deletedat', null);

      if (error) throw error;

      return data.map((student) => ({
        id: student.id,
        name: `${student.user.firstname} ${student.user.lastname}`,
        currentPoints: student.balance || 0,
        userId: student.user.id,
      }));
    } catch (error) {
      console.error('Error fetching active students:', error);
      throw error;
    }
  },

  async getAllTransactionTypes() {
    try {
      // Deleted types must not be offered for new transactions.
      const { data, error } = await supabase.from(TRANSACTION_TYPES_TABLE).select('*').is('deletedat', null);
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[TransactionService.getAllTransactionTypes]:', error);
      throw error;
    }
  },

  /**
   * Get transaction totals for a student
   */
  async getTransactionTotals(studentId: string): Promise<{ totalCredit: number; totalDebit: number }> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select(
          `
          type:transaction_types(type),
          amount
        `
        )
        .eq('studentid', studentId)
        .is('deletedat', null);

      if (error) throw error;

      return data.reduce(
        (acc, transaction) => {
          const amount = Number(transaction.amount) || 0;
          if (transaction.type?.type === 'credit') {
            acc.totalCredit += amount;
          } else if (transaction.type?.type === 'debit') {
            acc.totalDebit += amount;
          }
          return acc;
        },
        { totalCredit: 0, totalDebit: 0 }
      );
    } catch (error) {
      console.error('Error getting transaction totals:', error);
      return { totalCredit: 0, totalDebit: 0 };
    }
  },

  /**
   * Student responds to a pending transaction (approve/deny)
   */
  async respondToTransaction(
    transactionId: string,
    studentId: string,
    action: 'approve' | 'deny',
    options?: {
      payment_source?: 'balance' | 'savings';
      savings_account_id?: string;
    }
  ): Promise<void> {
    try {
      // 1. Get the transaction with type information
      const { data: transaction, error: transactionError } = await supabase
        .from(TABLE_NAME)
        .select(`
          *,
          transaction_type:transaction_types(*)
        `)
        .eq('id', transactionId)
        .eq('studentid', studentId) // Ensure student owns this transaction
        .single();

      if (transactionError) throw transactionError;
      if (!transaction) throw new Error('Transaction not found or not owned by student');

      // A deferred fine is still open: the student can pay or deny it until it is settled.
      if (!['PENDING_STUDENT_ACTION', 'DEFERRED'].includes(transaction.transaction_status)) {
        throw new Error('Transaction is not pending student action');
      }

      if (action === 'deny') {
        if (transaction.transaction_type?.charge_mode === 'bill') {
          throw new Error('A bill cannot be denied. Pay it, ask for more time, or ask a question.');
        }
        // Student denied the transaction - mark as rejected
        const { error: updateError } = await supabase
          .from(TABLE_NAME)
          .update({
            transaction_status: 'REJECTED',
            status: 'rejected',
            student_action: 'denied',
            student_action_date: new Date().toISOString(),
            updatedat: new Date().toISOString(),
          })
          .eq('id', transactionId);

        if (updateError) throw updateError;
        return;
      }

      // Student approved the transaction
      const isDebit = transaction.transaction_type.type !== 'credit';
      const paysFromSavings = isDebit && options?.payment_source === 'savings';
      if (paysFromSavings && !options?.savings_account_id) throw new Error('Choose a savings account to pay from');

      // Paying from savings: move the amount into the balance first. transfer_funds moves both sides
      // in one database call and refuses if the account is short, so nothing is half-moved.
      if (paysFromSavings) {
        await savingsAccountService.transferFunds({
          studentId,
          fromAccountId: options!.savings_account_id!,
          amount: transaction.amount,
        });
      }

      // 2. Get current student balance
      const { data: student, error: studentError } = await supabase
        .from('students')
        .select('balance')
        .eq('id', studentId)
        .single();

      if (studentError) throw studentError;
      if (!student) throw new Error('Student not found');

      // 3. Calculate new balance
      const balanceChange = transaction.transaction_type.type === 'credit' ?
        transaction.amount : -transaction.amount;
      const newBalance = (student.balance || 0) + balanceChange;
      if (isDebit && newBalance < 0) throw new Error('Not enough balance to pay this.');

      // 4. Update transaction status and student balance in a transaction
      const updates = [
        supabase
          .from(TABLE_NAME)
          .update({
            transaction_status: 'APPROVED',
            status: 'Approved',
            student_action: 'approved',
            student_action_date: new Date().toISOString(),
            balanceafter: newBalance,
            payment_method: options?.payment_source === 'savings' ? 'savings_transfer' : 'main_balance',
            savings_allocation: options?.savings_account_id ? JSON.stringify({ [options.savings_account_id]: transaction.amount }) : '{}',
            updatedat: new Date().toISOString(),
          })
          .eq('id', transactionId),

        supabase
          .from('students')
          .update({
            balance: newBalance,
            updatedat: new Date().toISOString(),
          })
          .eq('id', studentId)
      ];

      // Execute both updates
      for (const update of updates) {
        const { error } = await update;
        if (error) throw error;
      }

    } catch (error) {
      console.error('[TransactionService.respondToTransaction] Error:', error);
      throw error;
    }
  },

  /**
   * Student defers a transaction payment
   */
  async deferTransaction(
    transactionId: string,
    studentId: string,
    dueDate: Date,
    reason?: string
  ): Promise<void> {
    try {
      // 1. Validate the transaction belongs to student and is pending
      const { data: transaction, error: transactionError } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('id', transactionId)
        .eq('studentid', studentId)
        .single();

      if (transactionError) throw transactionError;
      if (!transaction) throw new Error('Transaction not found or not owned by student');

      if (transaction.transaction_status !== 'PENDING_STUDENT_ACTION') {
        throw new Error('Transaction is not pending student action');
      }

      // 2. Check if already deferred too many times (max 3 deferrals)
      const currentDeferrals = transaction.deferred_count || 0;
      if (currentDeferrals >= 3) {
        throw new Error('Maximum number of deferrals (3) reached for this transaction');
      }

      // 3. Validate due date is not more than 2 weeks from now
      const maxDueDate = new Date();
      maxDueDate.setDate(maxDueDate.getDate() + 14); // 2 weeks

      if (dueDate > maxDueDate) {
        throw new Error('Due date cannot be more than 2 weeks from now');
      }

      if (dueDate <= new Date()) {
        throw new Error('Due date must be in the future');
      }

      // 4. Update transaction status
      const { error: updateError } = await supabase
        .from(TABLE_NAME)
        .update({
          transaction_status: 'DEFERRED',
          student_action: 'deferred',
          student_action_date: new Date().toISOString(),
          due_date: dueDate.toISOString(),
          deferred_count: currentDeferrals + 1,
          reason: reason || `Deferred until ${dueDate.toLocaleDateString()}`,
          updatedat: new Date().toISOString(),
        })
        .eq('id', transactionId);

      if (updateError) throw updateError;

    } catch (error) {
      console.error('[TransactionService.deferTransaction] Error:', error);
      throw error;
    }
  },

  /**
   * Student questions a transaction
   */
  async questionTransaction(
    transactionId: string,
    studentId: string,
    questionText: string
  ): Promise<void> {
    try {
      // 1. Validate the transaction belongs to student and is pending
      const { data: transaction, error: transactionError } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('id', transactionId)
        .eq('studentid', studentId)
        .single();

      if (transactionError) throw transactionError;
      if (!transaction) throw new Error('Transaction not found or not owned by student');

      if (transaction.transaction_status !== 'PENDING_STUDENT_ACTION') {
        throw new Error('Transaction is not pending student action');
      }

      // 2. Update transaction status
      const { error: updateError } = await supabase
        .from(TABLE_NAME)
        .update({
          transaction_status: 'QUESTIONED',
          student_action: 'questioned',
          student_action_date: new Date().toISOString(),
          reason: questionText,
          updatedat: new Date().toISOString(),
        })
        .eq('id', transactionId);

      if (updateError) throw updateError;

    } catch (error) {
      console.error('[TransactionService.questionTransaction] Error:', error);
      throw error;
    }
  },

  /**
   * Teacher overrides a student transaction decision
   */
  async overrideTransaction(
    transactionId: string,
    teacherId: string,
    action: 'approve' | 'cancel',
    reason: string
  ): Promise<void> {
    try {
      // 1. Get the transaction
      const { data: transaction, error: transactionError } = await supabase
        .from(TABLE_NAME)
        .select(`
          *,
          transaction_type:transaction_types(*)
        `)
        .eq('id', transactionId)
        .single();

      if (transactionError) throw transactionError;
      if (!transaction) throw new Error('Transaction not found');

      if (action === 'cancel') {
        // Teacher cancelled the transaction
        const { error: updateError } = await supabase
          .from(TABLE_NAME)
          .update({
            transaction_status: 'CANCELLED',
            status: 'cancelled',
            teacher_override_reason: reason,
            updatedat: new Date().toISOString(),
          })
          .eq('id', transactionId);

        if (updateError) throw updateError;
        return;
      }

      // Teacher force-approved: collect it from the student's balance in one database step.
      await transactionService.collectTransaction(transactionId, reason);

    } catch (error) {
      console.error('[TransactionService.overrideTransaction] Error:', error);
      throw error;
    }
  },

  /**
   * Staff pay a student's open charge for them from the student's balance (the "Collect now" button).
   * collect_transaction refuses a charge that is no longer open, so a second click cannot charge twice.
   * Returns the student's new balance.
   */
  async collectTransaction(transactionId: string, reason?: string): Promise<number> {
    const { data, error } = await supabase.rpc('collect_transaction', {
      p_transaction_id: transactionId,
      p_reason: reason ?? null,
    });
    if (error) throw error;
    return Number(data);
  },

  /**
   * Get pending transactions for a student
   */
  async getPendingTransactions(studentId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select(`
          *,
          transaction_type:transaction_types(*)
        `)
        .eq('studentid', studentId)
        .in('transaction_status', ['PENDING_STUDENT_ACTION', 'DEFERRED'])
        .is('deletedat', null)
        .order('createdat', { ascending: false });

      if (error) throw error;
      return data || [];

    } catch (error) {
      console.error('[TransactionService.getPendingTransactions] Error:', error);
      throw error;
    }
  },

  /**
   * Get transaction summary for a student
   */
  async getStudentTransactionSummary(studentId: string): Promise<{
    pendingAmount: number;
    overdueAmount: number;
    totalLateFees: number;
    pendingCount: number;
    overdueCount: number;
  }> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('transaction_status, amount, late_fee_total, due_date')
        .eq('studentid', studentId)
        .is('deletedat', null);

      if (error) throw error;

      const now = new Date();
      const summary = {
        pendingAmount: 0,
        overdueAmount: 0,
        totalLateFees: 0,
        pendingCount: 0,
        overdueCount: 0,
      };

      data?.forEach(transaction => {
        const amount = Number(transaction.amount) || 0;
        const lateFee = Number(transaction.late_fee_total) || 0;

        if (transaction.transaction_status === 'PENDING_STUDENT_ACTION') {
          summary.pendingAmount += amount;
          summary.pendingCount += 1;
        } else if (transaction.transaction_status === 'DEFERRED') {
          if (transaction.due_date && new Date(transaction.due_date) < now) {
            summary.overdueAmount += amount;
            summary.overdueCount += 1;
          } else {
            summary.pendingAmount += amount;
            summary.pendingCount += 1;
          }
        }

        summary.totalLateFees += lateFee;
      });

      return summary;

    } catch (error) {
      console.error('[TransactionService.getStudentTransactionSummary] Error:', error);
      throw error;
    }
  },

  /**
   * Get a transaction type by ID
   */
  async getTransactionTypeById(id: string) {
    try {
      const { data, error } = await supabase.from(TRANSACTION_TYPES_TABLE).select('*').eq('id', id).single();
      console.log(data);

      if (error) throw error;

      if (!data) {
        throw new Error('Transaction type not found');
      }

      return {
        id: data.id,
        name: data.name,
        description: data.description,
        type: data.type as 'debit' | 'credit',
        points: data.points,
        priceType: data.priceType as 'fixed' | 'variable',
        chargeMode: data.charge_mode as ChargeMode,
      };
    } catch (error) {
      console.error('[TransactionService.getTransactionTypeById]:', error);
      throw error;
    }
  },

  /**
   * Get pending transactions for teacher/admin dashboard
   */
  async getPendingTransactionsForTeacher(teacherId: string, classIds?: string[]): Promise<any[]> {
    try {
      let query = supabase
        .from(TABLE_NAME)
        .select(`
          *,
          transaction_type:transaction_types(*),
          student:students!inner(
            id,
            user:users!inner(
              id,
              firstname,
              lastname
            )
          )
        `)
        .in('transaction_status', ['PENDING_STUDENT_ACTION', 'DEFERRED', 'QUESTIONED'])
        .is('deletedat', null);

      // For admin role, get all pending transactions
      // For teacher role, only get transactions for their students
      if (teacherId && classIds && classIds.length > 0) {
        // If specific class IDs are provided, filter by those classes
        query = query.in('student.classid', classIds);
      } else if (teacherId) {
        // For teacher role, we would need to join with class enrollment to get their students
        // For now, we'll get all pending transactions (admin behavior)
        // TODO: Implement proper teacher-student relationship filtering
      }

      query = query.order('createdat', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('[TransactionService.getPendingTransactionsForTeacher] Error:', error);
        throw error;
      }

      return data || [];

    } catch (error) {
      console.error('[TransactionService.getPendingTransactionsForTeacher] Error:', error);
      throw error;
    }
  },

  /**
   * Send reminder/nudge to student about pending transaction
   */
  async sendStudentReminder(transactionId: string, teacherId: string): Promise<void> {
    try {
      // This is a placeholder for notification service integration
      // In a full implementation, this would:
      // 1. Create a notification record
      // 2. Send email/SMS reminder
      // 3. Update transaction with nudge timestamp
      console.log(`[TransactionService.sendStudentReminder] Sending reminder for transaction ${transactionId} by teacher ${teacherId}`);

      // For now, just log the action
      const { error } = await supabase
        .from(TABLE_NAME)
        .update({
          extendedproperties: supabase.raw(`
            COALESCE(extendedproperties, '{}'::jsonb) ||
            jsonb_build_object('lastReminder', NOW()::text, 'reminderCount',
            COALESCE((extendedproperties->>'reminderCount')::int, 0) + 1)
          `),
          updatedat: new Date().toISOString(),
        })
        .eq('id', transactionId);

      if (error) throw error;

    } catch (error) {
      console.error('[TransactionService.sendStudentReminder] Error:', error);
      throw error;
    }
  },
};
